import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, RadioGroup } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { AssignablePartnerListsDocument } from '../../../graphql/__generated__/types';
import { partnerListEmptiness } from './catalogue';
import { Draft } from './draft';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
}

/**
 * The value of the « no list » choice. A radio cannot be unticked, and the step has
 * to stay leavable with nothing granted, so « aucune » is an option one picks,
 * pre-selected, rather than a state one falls into. Same reading as the perimeter
 * of step 2: shown pre-selected, which makes it a choice made on screen instead of
 * a silent default. It maps back to `partnerListId: null`, and no third state ever
 * reaches the API.
 */
const NO_LIST = '';

/**
 * Step 4: the partner list, an **optional** attribution.
 *
 * Attention, empty-first for the same reason as step 3: `partner_list` had no writer until
 * PCG-79, so this catalogue was empty on every environment. Lists can now be declared
 * from the « Listes de partenaires » screen, but the catalogue starts empty on a fresh
 * environment and the empty state remains the one most operators will meet first.
 *
 * One list at most, because `InviteUserInput` carries a single `partnerListId`:
 * the base would accept several, the invitation takes one. The habilitation hangs
 * from the **person** and not from a membership: the discovery module has no
 * account axis, so a list per attachment would be unobservable. There is therefore
 * no grouping by account here, and its absence is a decision rather than an
 * oversight.
 */
const PartnerListStep = ({ draft, onChange }: Props) => {
    const { data, loading, error, refetch } = useQuery(AssignablePartnerListsDocument);

    const lists = (data?.assignablePartnerLists?.lists ?? []).filter(Boolean);
    const emptiness = partnerListEmptiness(error, data?.assignablePartnerLists?.emptyReason);

    /**
     * The count is the number of **rows of the list**, and not what the person will
     * see: delisted partners filter the module's view without leaving any list. It
     * is shown because zero is the value that changes a decision: granting an empty
     * list opens a module with nothing in it.
     */
    const options = [
        {
            value: NO_LIST,
            label: c('Onboarding partners').t`No list — the « Partenaires » module stays empty for this person`,
        },
        ...lists.map((list) => {
            const count = list.partnerCount ?? 0;
            const name = list.name ?? '';
            const size =
                count === 0
                    ? c('Onboarding partners').t`no partner yet`
                    : ngettext(msgid`${count} partner`, `${count} partners`, count);
            return { value: list.id as string, label: `${name} · ${size}` };
        }),
    ];

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding partners')
                    .t`Associate a partner list`}</h2>
                <p className="mt-1 text-xs text-text-600">
                    {c('Onboarding partners')
                        .t`The chosen list decides which partners the person may consult in the « Partenaires » module of the portal. Optional step: without a list the module stays empty for them.`}
                </p>
            </div>

            {loading && <ListSkeleton rows={3} withControl />}

            {!loading && emptiness && (
                <Alert type={emptiness.tone} title={emptiness.title}>
                    <p className="text-sm">{emptiness.guidance}</p>
                    {emptiness.detail && (
                        <p className="mt-2 break-words font-mono text-xs text-text-600">{emptiness.detail}</p>
                    )}
                    {/* Wrapped for the same reason as step 3: the alert stacks its
                        children, and a bare button would stretch across it. */}
                    {error && (
                        <div className="mt-2">
                            <Button category="weak" size="small" onClick={() => refetch()}>
                                {c('Action').t`Retry`}
                            </Button>
                        </div>
                    )}
                </Alert>
            )}

            {/* The « aucune » choice is offered even when the catalogue is empty: it is
                the answer the step is about to record, and hiding the only option would
                leave the block above looking like the whole step. */}
            <RadioGroup
                name="partner-list"
                value={draft.partnerListId ?? NO_LIST}
                onValue={(value: string) => onChange({ partnerListId: value === NO_LIST ? null : value })}
                options={options}
            />
        </div>
    );
};

export default PartnerListStep;
